/* Recipe SEO Audit - checks pasted page HTML or JSON-LD client-side. */
(function () {
  'use strict';

  var EXAMPLE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: 'Spicy Honey Hot Sauce Wings',
    description: 'Crispy baked wings tossed in a sticky hot honey glaze.',
    image: ['https://example.com/images/hot-honey-wings.jpg'],
    author: { '@type': 'Organization', name: 'Mosaic Pantry' },
    datePublished: '2026-02-12',
    prepTime: 'PT15M',
    cookTime: 'PT40M',
    totalTime: 'PT55M',
    recipeYield: '4 servings',
    recipeCategory: 'Appetizer',
    recipeCuisine: 'American',
    keywords: 'hot honey wings, hot sauce wings, game day appetizer',
    recipeIngredient: [
      '2 lbs chicken wings',
      '1/3 cup honey',
      '1/4 cup hot sauce',
      '2 tbsp butter'
    ],
    recipeInstructions: [
      { '@type': 'HowToStep', text: 'Preheat oven to 425F and line a baking sheet.' },
      { '@type': 'HowToStep', text: 'Season the wings, then bake until crisp.' },
      { '@type': 'HowToStep', text: 'Whisk honey, hot sauce, and butter into a glaze.' }
    ],
    nutrition: { '@type': 'NutritionInformation', calories: '380 calories' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '127' }
  };

  var EXAMPLE_HTML =
    '<article class="recipe">\n' +
    '<h1>Spicy Honey Hot Sauce Wings</h1>\n' +
    '<p>Crispy baked wings tossed in a sticky hot honey glaze.</p>\n' +
    '<ul><li>2 lbs chicken wings</li><li>1/3 cup honey</li><li>1/4 cup hot sauce</li></ul>\n' +
    '<a href="/products/hot-sauce">Shop the hot sauce</a>\n' +
    '<script type="application/ld+json">\n' +
    JSON.stringify(EXAMPLE_SCHEMA, null, 2) +
    '\n<\/script>\n' +
    '</article>';

  function normalizeType(type) {
    if (Array.isArray(type)) return type.map(normalizeType).join(',');
    return String(type || '').toLowerCase();
  }

  function asArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function plainText(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (value.text) return plainText(value.text);
    if (value.name) return plainText(value.name);
    if (Array.isArray(value.itemListElement)) {
      return value.itemListElement.map(plainText).join(' ');
    }
    return '';
  }

  function extractJsonObjects(value, found) {
    if (!value || typeof value !== 'object') return;
    if (normalizeType(value['@type']).indexOf('recipe') !== -1) {
      found.push(value);
    }
    Object.keys(value).forEach(function (key) {
      var child = value[key];
      if (Array.isArray(child)) {
        child.forEach(function (item) { extractJsonObjects(item, found); });
      } else if (child && typeof child === 'object') {
        extractJsonObjects(child, found);
      }
    });
  }

  function parseJsonCandidate(text) {
    var trimmed = text.trim();
    if (!trimmed) return null;
    if (trimmed.indexOf('<script') !== -1) return null;
    if (trimmed.charAt(0) !== '{' && trimmed.charAt(0) !== '[') return null;
    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return null;
    }
  }

  function parseHtml(text) {
    var parser = new DOMParser();
    return parser.parseFromString(text, 'text/html');
  }

  function extractRecipes(source) {
    var recipes = [];
    var json = parseJsonCandidate(source);
    var doc = null;
    var pageText = '';
    var productSignals = 0;

    if (json) {
      extractJsonObjects(json, recipes);
    } else {
      doc = parseHtml(source);
      pageText = (doc.body && doc.body.textContent ? doc.body.textContent : '').replace(/\s+/g, ' ').trim();
      productSignals = doc.querySelectorAll('a[href*="/products/"], form[action*="/cart/add"], [data-product-id], [data-variant-id]').length;
      Array.prototype.slice.call(doc.querySelectorAll('script[type="application/ld+json"]')).forEach(function (script) {
        try {
          extractJsonObjects(JSON.parse(script.textContent), recipes);
        } catch (error) {
          /* Skip invalid JSON-LD blocks and report no recipe if none parse. */
        }
      });
    }

    return {
      recipe: recipes[0] || null,
      recipeCount: recipes.length,
      pageText: pageText,
      productSignals: productSignals,
      sourceLooksLikeHtml: /<\/?[a-z][\s\S]*>/i.test(source)
    };
  }

  function hasValue(value) {
    if (Array.isArray(value)) return value.length > 0 && value.some(hasValue);
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(String(value || '').trim());
  }

  function countInstructions(recipe) {
    return asArray(recipe.recipeInstructions).map(plainText).filter(Boolean).length;
  }

  function countIngredients(recipe) {
    return asArray(recipe.recipeIngredient).map(plainText).filter(Boolean).length;
  }

  function checkVisibleMatch(recipe, pageText) {
    if (!pageText) return null;
    var lower = pageText.toLowerCase();
    var name = plainText(recipe.name).toLowerCase();
    var ingredients = asArray(recipe.recipeIngredient).map(plainText).filter(Boolean).slice(0, 3);
    var matchedIngredients = ingredients.filter(function (ingredient) {
      return lower.indexOf(ingredient.toLowerCase().slice(0, 24)) !== -1;
    }).length;
    return Boolean(name && lower.indexOf(name) !== -1 && (!ingredients.length || matchedIngredients >= Math.min(2, ingredients.length)));
  }

  function buildChecks(audit) {
    var recipe = audit.recipe;
    var visibleMatch = recipe ? checkVisibleMatch(recipe, audit.pageText) : null;
    if (!recipe) {
      return [
        {
          title: 'Recipe schema detected',
          detail: 'No Schema.org Recipe JSON-LD was found in the pasted content.',
          state: 'fail',
          points: 0,
          max: 30,
          fix: 'Add valid Recipe JSON-LD to the recipe page, or use the schema generator to create it.'
        }
      ];
    }

    return [
      {
        title: 'Recipe schema detected',
        detail: audit.recipeCount === 1 ? 'One Recipe schema block was found.' : audit.recipeCount + ' Recipe schema blocks were found. Usually one primary recipe is cleaner.',
        state: audit.recipeCount === 1 ? 'pass' : 'warn',
        points: audit.recipeCount === 1 ? 30 : 22,
        max: 30,
        fix: audit.recipeCount === 1 ? '' : 'Keep one primary Recipe schema block on the page unless there are truly multiple recipes.'
      },
      {
        title: 'Required rich result fields',
        detail: 'Google requires a recipe name and image for recipe rich result eligibility.',
        state: hasValue(recipe.name) && hasValue(recipe.image) ? 'pass' : 'fail',
        points: (hasValue(recipe.name) ? 10 : 0) + (hasValue(recipe.image) ? 10 : 0),
        max: 20,
        fix: 'Add both a recipe name and at least one crawlable image URL to the Recipe schema.'
      },
      {
        title: 'Ingredients and instructions',
        detail: countIngredients(recipe) + ' ingredients and ' + countInstructions(recipe) + ' instruction steps found.',
        state: countIngredients(recipe) && countInstructions(recipe) ? 'pass' : 'fail',
        points: (countIngredients(recipe) ? 10 : 0) + (countInstructions(recipe) ? 10 : 0),
        max: 20,
        fix: 'Include the full ingredient list and step-by-step instructions in structured data.'
      },
      {
        title: 'Time, yield, and nutrition depth',
        detail: 'Prep/cook time, yield, and nutrition help searchers evaluate the recipe before they click.',
        state: hasValue(recipe.prepTime) && hasValue(recipe.cookTime) && hasValue(recipe.recipeYield) ? 'pass' : 'warn',
        points: (hasValue(recipe.prepTime) ? 3 : 0) + (hasValue(recipe.cookTime) ? 3 : 0) + (hasValue(recipe.totalTime) ? 2 : 0) + (hasValue(recipe.recipeYield) ? 4 : 0) + (hasValue(recipe.nutrition) ? 3 : 0),
        max: 15,
        fix: 'Add prep time, cook time, total time, recipe yield, and nutrition where accurate.'
      },
      {
        title: 'Ratings and authority signals',
        detail: 'Ratings, author, and publish date make the recipe feel more complete and trustworthy.',
        state: hasValue(recipe.aggregateRating) && hasValue(recipe.author) ? 'pass' : 'warn',
        points: (hasValue(recipe.aggregateRating) ? 6 : 0) + (hasValue(recipe.author) ? 3 : 0) + (hasValue(recipe.datePublished) ? 2 : 0),
        max: 11,
        fix: 'Add aggregate ratings only if ratings are visible on the page. Add author and publish date when available.'
      },
      {
        title: 'Schema matches visible content',
        detail: visibleMatch === null ? 'Paste full page HTML to check whether schema matches visible recipe content.' : visibleMatch ? 'The recipe name and sample ingredients appear in the visible page text.' : 'The schema may not match the visible page text.',
        state: visibleMatch === null ? 'warn' : visibleMatch ? 'pass' : 'fail',
        points: visibleMatch === null ? 4 : visibleMatch ? 9 : 0,
        max: 9,
        fix: 'Make sure the recipe name, ingredients, and instructions in schema match what visitors can read on the page.'
      },
      {
        title: 'Shoppable recipe opportunity',
        detail: audit.productSignals ? audit.productSignals + ' product or cart signals found in the pasted HTML.' : 'No product links or cart signals were detected in the pasted content.',
        state: audit.productSignals ? 'pass' : 'warn',
        points: audit.productSignals ? 5 : 0,
        max: 5,
        fix: 'Link key ingredients to Shopify products so recipe traffic can move toward the cart.'
      }
    ];
  }

  function scoreChecks(checks) {
    var points = checks.reduce(function (sum, check) { return sum + check.points; }, 0);
    var max = checks.reduce(function (sum, check) { return sum + check.max; }, 0);
    return max ? Math.round((points / max) * 100) : 0;
  }

  function grade(score) {
    if (score >= 90) return 'Strong';
    if (score >= 75) return 'Good';
    if (score >= 55) return 'Needs work';
    return 'Not ready';
  }

  function summarize(score, audit) {
    if (!audit.recipe) return 'No Recipe schema was found. This page is not eligible for recipe rich results until schema is added.';
    if (score >= 90) return 'This recipe has the core structured data Google expects, plus useful depth for searchers.';
    if (score >= 75) return 'This recipe has the basics in place. A few missing fields may limit how rich the result can be.';
    if (score >= 55) return 'This recipe has partial schema, but important rich-result fields need attention.';
    return 'This recipe needs schema cleanup before it is ready to compete in recipe search.';
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderChecks(checks) {
    return checks.map(function (check) {
      return (
        '<li class="rk-audit__check" data-state="' + check.state + '">' +
        '<span class="rk-audit__check-dot"></span>' +
        '<span><strong class="rk-audit__check-title">' + escapeHtml(check.title) + '</strong>' +
        '<span class="rk-audit__check-detail">' + escapeHtml(check.detail) + '</span></span>' +
        '<span class="rk-audit__check-badge">' + check.points + '/' + check.max + '</span>' +
        '</li>'
      );
    }).join('');
  }

  function renderFixes(checks) {
    var fixes = checks
      .filter(function (check) { return check.state !== 'pass' && check.fix; })
      .sort(function (a, b) { return (b.max - b.points) - (a.max - a.points); })
      .slice(0, 4);
    if (!fixes.length) {
      fixes = [{ fix: 'Keep schema synchronized whenever you edit the visible recipe.' }];
    }
    return fixes.map(function (check) {
      return '<li>' + escapeHtml(check.fix) + '</li>';
    }).join('');
  }

  function init(root) {
    var urlInput = root.querySelector('[data-ref="url"]');
    var sourceInput = root.querySelector('[data-ref="source"]');
    var runButton = root.querySelector('[data-ref="run"]');
    var exampleButton = root.querySelector('[data-ref="example"]');
    var clearButton = root.querySelector('[data-ref="clear"]');
    var scoreEl = root.querySelector('[data-ref="score"]');
    var gradeEl = root.querySelector('[data-ref="grade"]');
    var summaryEl = root.querySelector('[data-ref="summary"]');
    var checksEl = root.querySelector('[data-ref="checks"]');
    var fixesEl = root.querySelector('[data-ref="fixes"]');
    if (!sourceInput || !runButton || !scoreEl || !gradeEl || !summaryEl || !checksEl || !fixesEl) return;

    var fetchUrl =
      (root.getAttribute('data-fetch-url') || '').trim() ||
      'https://recipe-seo-audit-fetch.recipekit.workers.dev';

    function applyAudit(source, pageUrl) {
      var audit = extractRecipes(source);
      var checks = buildChecks(audit);
      var score = scoreChecks(checks);
      scoreEl.textContent = String(score);
      gradeEl.textContent = grade(score);
      summaryEl.textContent = (pageUrl ? pageUrl + ': ' : '') + summarize(score, audit);
      checksEl.innerHTML = renderChecks(checks);
      fixesEl.innerHTML = renderFixes(checks);
    }

    function setEmptyState() {
      scoreEl.textContent = '--';
      gradeEl.textContent = 'Paste a recipe URL or HTML to begin';
      summaryEl.textContent = 'Enter a recipe URL (we will fetch the page for you) or paste the page HTML or Recipe JSON-LD.';
      checksEl.innerHTML = '';
      fixesEl.innerHTML = '<li>Enter a URL or paste a recipe page to see the highest-impact fixes.</li>';
    }

    function setLoadingState(url) {
      scoreEl.textContent = '…';
      gradeEl.textContent = 'Fetching…';
      summaryEl.textContent = 'Fetching ' + url + ' so we can read its schema and content.';
      checksEl.innerHTML = '';
      fixesEl.innerHTML = '<li>Hang tight — fetching the page now.</li>';
    }

    function setErrorState(message) {
      scoreEl.textContent = '--';
      gradeEl.textContent = "Couldn't fetch that URL";
      summaryEl.textContent = message;
      checksEl.innerHTML = '';
      fixesEl.innerHTML = '<li>Paste the page HTML manually into the box above and run the audit.</li>';
    }

    async function runAudit() {
      var pasted = (sourceInput.value || '').trim();
      var urlValue = urlInput && urlInput.value ? urlInput.value.trim() : '';

      // Path 1: textarea has content. Audit that, ignore URL fetching.
      if (pasted) {
        applyAudit(pasted, urlValue);
        if (window.posthog) window.posthog.capture('recipe_seo_audit_completed', { audit_source: 'pasted_content' });
        return;
      }
      // Path 2: only a URL. Fetch the page via the worker, then audit.
      if (urlValue) {
        var u;
        try { u = new URL(urlValue); } catch (_e) {
          setErrorState('That does not look like a valid http(s) URL.');
          return;
        }
        if (u.protocol !== 'http:' && u.protocol !== 'https:') {
          setErrorState('Only http(s) URLs are supported.');
          return;
        }
        setLoadingState(urlValue);
        runButton.disabled = true;
        try {
          var res = await fetch(fetchUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: urlValue }),
          });
          var body = await res.json().catch(function () { return {}; });
          if (!res.ok || !body.ok || !body.html) {
            setErrorState(body.error || 'The fetch worker returned an error. Try again or paste the HTML.');
            return;
          }
          // Show the fetched HTML so the merchant can see what we audited.
          sourceInput.value = body.html;
          applyAudit(body.html, urlValue);
          if (window.posthog) window.posthog.capture('recipe_seo_audit_completed', { audit_source: 'url_fetch' });
        } catch (err) {
          setErrorState("Couldn't reach the fetch service. Try again, or paste the page HTML.");
        } finally {
          runButton.disabled = false;
        }
        return;
      }
      // Path 3: nothing entered.
      setEmptyState();
    }

    runButton.addEventListener('click', runAudit);

    sourceInput.addEventListener('input', function () {
      // Only auto-audit on paste; never debounce-fetch URLs.
      if (!sourceInput.value.trim()) return;
      window.clearTimeout(sourceInput.auditTimer);
      sourceInput.auditTimer = window.setTimeout(function () {
        var urlValue = urlInput && urlInput.value ? urlInput.value.trim() : '';
        applyAudit(sourceInput.value, urlValue);
      }, 350);
    });

    if (exampleButton) {
      exampleButton.addEventListener('click', function () {
        if (urlInput) urlInput.value = 'https://example.com/blogs/recipes/hot-honey-wings';
        sourceInput.value = EXAMPLE_HTML;
        applyAudit(EXAMPLE_HTML, 'https://example.com/blogs/recipes/hot-honey-wings');
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        if (urlInput) urlInput.value = '';
        sourceInput.value = '';
        setEmptyState();
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    Array.prototype.slice
      .call(document.querySelectorAll('[data-rk-recipe-seo-audit]'))
      .forEach(init);
  });
})();
